import { useState, useEffect, useRef, useMemo } from "react";
import QRCode from "qrcode";
import { createSlug } from "@/lib/slug";
import { supabase, SanPhamHanQuoc, PatentCertification, CategoryRow, SubCategoryRow } from "../../../lib/supabase";
import ImageUploader from "../../../components/base/ImageUploader";
import MediaUploader from "../../../components/base/MediaUploader";
import RichTextEditor from "../../../components/base/RichTextEditor";
import SectionsRepeater, { SectionItem } from "./SectionsRepeater";
import VisualBuilder from "./builder/VisualBuilder";
import BuilderPreviewModal from "./builder/BuilderPreviewModal";
import { BuilderPage } from "./builder/types";
import { FlipbookImageSorter } from "./FlipbookImageSorter";

type Lang = "vi" | "kr";

interface Props {
  lang: Lang;
  editData?: SanPhamHanQuoc | null;
  onClose: () => void;
  onSuccess: () => void;
}

type FormData = Omit<SanPhamHanQuoc, "created_at" | "updated_at" | "images" | "patents_certifications"> & {
  tagsInput: string;
  specsInput: string;
  parent_category_id?: string;
  display_mode?: "default" | "advanced";
};

const emptyForm = (): FormData => ({
  id: "",
  name: "",
  name_kr: "",
  category_id: "",
  category_name: "",
  sub_category_name: "",
  company_name: "",
  company_code: "",
  company_logo: "",
  company_id: "",
  manager_code: "",
  manager_phone: "",
  manager_email: "",
  price: "",
  description: "",
  description_detail: "",
  registered_date: new Date().toISOString().slice(0, 10),
  is_featured: false,
  original_link: "",
  card_link: "",
  hero_tagline: "",
  sections: [],
  specs: [],
  tagsInput: "",
  specsInput: "",
  display_mode: "default",
});

// Add New Category Modal
interface AddCategoryModalProps {
  lang: Lang;
  onClose: () => void;
  onSaved: (cat: CategoryRow) => void;
}

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({ lang, onClose, onSaved }) => {
  const t = (vi: string, kr: string) => (lang === "vi" ? vi : kr);
  const [name, setName] = useState("");
  const [nameKr, setNameKr] = useState("");
  const [icon, setIcon] = useState("ri-grid-line");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!name.trim()) { setError(t("Vui lòng nhập tên danh mục!", "카테고리 이름을 입력해주세요!")); return; }
    setSaving(true);
    const id = `cat-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 20)}-${Date.now()}`;
    const { error: err } = await supabase.from("categories").insert({
      id,
      name: name.trim(),
      name_kr: nameKr.trim() || name.trim(),
      icon: icon.trim() || "ri-grid-line",
      description: description.trim(),
      display_order: 99,
    });
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved({ id, name: name.trim(), name_kr: nameKr.trim(), icon: icon.trim(), description: description.trim() });
  };

  const ICON_OPTIONS = [
    "ri-grid-line", "ri-test-tube-line", "ri-car-line", "ri-cpu-line",
    "ri-tools-line", "ri-battery-2-charge-line", "ri-building-4-line",
    "ri-t-shirt-2-line", "ri-flask-line", "ri-leaf-line", "ri-recycle-line",
    "ri-shield-check-line", "ri-rocket-line", "ri-water-flash-line",
  ];

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-800">{t("Thêm danh mục mới", "새 카테고리 추가")}</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}
          <div>
            <label className={labelCls}>{t("Tên danh mục (VI) *", "카테고리명 (VI) *")}</label>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("VD: Thiết Bị Điện Tử", "예: 전자 기기")} />
          </div>
          <div>
            <label className={labelCls}>{t("Tên danh mục (KR)", "카테고리명 (KR)")}</label>
            <input className={inputCls} value={nameKr} onChange={(e) => setNameKr(e.target.value)} placeholder="예: 전자 기기" />
          </div>
          <div>
            <label className={labelCls}>{t("Icon (Remix Icon class)", "아이콘 (Remix Icon 클래스)")}</label>
            <div className="flex gap-2 flex-wrap mb-2">
              {ICON_OPTIONS.map((ic) => (
                <button
                  key={ic}
                  type="button"
                  onClick={() => setIcon(ic)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${
                    icon === ic ? "border-[#004A99] bg-[#004A99]/10 text-[#004A99]" : "border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  <i className={ic} />
                </button>
              ))}
            </div>
            <input className={inputCls} value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="ri-grid-line" />
          </div>
          <div>
            <label className={labelCls}>{t("Mô tả ngắn", "간단한 설명")}</label>
            <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap transition-colors">{t("Hủy", "취소")}</button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-[#004A99] text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2">
            {saving && <i className="ri-loader-4-line animate-spin" />}
            {t("Lưu danh mục", "저장")}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductFormModal: React.FC<Props> = ({ lang, editData, onClose, onSuccess }) => {
  const t = (vi: string, kr: string) => (lang === "vi" ? vi : kr);
  const [form, setForm] = useState<FormData>(emptyForm());
  const [images, setImages] = useState<string[]>([]);
  const [mediaItems, setMediaItems] = useState<{ url: string; type: "image" | "video" }[]>([]);
  const [patents, setPatents] = useState<PatentCertification[]>([]);
  const [patentImages, setPatentImages] = useState<Record<number, string[]>>({});
  const [sections, setSections] = useState<SectionItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<"basic" | "media" | "sections" | "applications" | "patents" | "catalog" | "detail" | "flipbook">("basic");
  const [applications, setApplications] = useState<{ title: string; description: string; image_url: string }[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategoryRow[]>([]);
  const [filteredSubCats, setFilteredSubCats] = useState<SubCategoryRow[]>([]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [pdfParsing, setPdfParsing] = useState(false);
  const [pdfMessage, setPdfMessage] = useState("");
  const [builderPages, setBuilderPages] = useState<BuilderPage[]>([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [catalogBuilderPages, setCatalogBuilderPages] = useState<BuilderPage[]>([]);
  const [showCatalogPreviewModal, setShowCatalogPreviewModal] = useState(false);
  const [pdfConverting, setPdfConverting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfPreviewImages, setPdfPreviewImages] = useState<string[]>([]);
  const [flipbookImages, setFlipbookImages] = useState<string[]>([]);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const pdfConvertInputRef = useRef<HTMLInputElement>(null);
  const [logoAutoFilled, setLogoAutoFilled] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [qrCopied, setQrCopied] = useState(false);

  const flipbookQrUrl = useMemo(() => {
    if (!form.name || !form.company_name) return "";
    const cSlug = createSlug(form.company_name);
    const pSlug = createSlug(form.name);
    return `https://kcania.vn/flipbook/catalog/${cSlug}/${pSlug}`;
  }, [form.name, form.company_name]);
  const logoAutoFilledRef = useRef(false);
  const companyLogoRef = useRef(form.company_logo);
  logoAutoFilledRef.current = logoAutoFilled;
  companyLogoRef.current = form.company_logo;

  // Generate QR code whenever URL changes
  useEffect(() => {
    if (!flipbookQrUrl) { setQrDataUrl(""); return; }
    QRCode.toDataURL(flipbookQrUrl, {
      width: 512,
      margin: 2,
      errorCorrectionLevel: "H",
      color: { dark: "#1a1a2e", light: "#ffffff" },
    }).then(setQrDataUrl).catch(() => setQrDataUrl(""));
  }, [flipbookQrUrl]);

  const handleDownloadQR = () => {
    if (!qrDataUrl) return;
    const link = document.createElement("a");
    const safeName = (form.name || "flipbook").replace(/[^a-z0-9]/gi, "-").toLowerCase();
    link.download = `qr-flipbook-${safeName}.png`;
    link.href = qrDataUrl;
    link.click();
  };

  const handleCopyQrUrl = () => {
    if (!flipbookQrUrl) return;
    navigator.clipboard.writeText(flipbookQrUrl).then(() => {
      setQrCopied(true);
      setTimeout(() => setQrCopied(false), 2000);
    });
  };

  useEffect(() => {
    const fetchCategories = async () => {
      const [catRes, subCatRes] = await Promise.all([
        supabase.from("categories").select("*").order("display_order", { ascending: true }),
        supabase.from("sub_categories").select("*").order("display_order", { ascending: true }),
      ]);
      if (catRes.data) setCategories(catRes.data as CategoryRow[]);
      if (subCatRes.data) setSubCategories(subCatRes.data as SubCategoryRow[]);
    };
    fetchCategories();
  }, []);

  // Auto-fetch company logo from existing products or companies table
  useEffect(() => {
    const name = form.company_name?.trim();
    if (!name) return;
    if (companyLogoRef.current && !logoAutoFilledRef.current) return;
    const timer = setTimeout(async () => {
      // Step 1: search other products with same company_name
      const { data: products } = await supabase
        .from("san_pham_han_quoc")
        .select("company_logo")
        .eq("company_name", name)
        .not("company_logo", "is", null)
        .limit(1);
      if (products && products.length > 0 && products[0].company_logo) {
        set("company_logo", products[0].company_logo);
        setLogoAutoFilled(true);
        return;
      }
      // Step 2: fallback to companies table
      const { data: company } = await supabase
        .from("companies")
        .select("logo_url")
        .eq("name", name)
        .maybeSingle();
      if (company?.logo_url) {
        set("company_logo", company.logo_url);
        setLogoAutoFilled(true);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [form.company_name]);

  useEffect(() => {
    if (editData) {
      setForm({
        ...emptyForm(),
        ...editData,
        tagsInput: (editData.tags ?? []).join(", "),
        specsInput: (editData.specs ?? []).map(s => `${s.label}:${s.value}`).join("\n"),
        display_mode: (editData.display_mode as "default" | "advanced") || "default",
      });
      setImages(editData.images ?? []);
      // Load media items (images + videos)
      const savedMedia = (editData as Record<string, unknown>).media_items as { url: string; type: "image" | "video" }[] | undefined;
      if (savedMedia && savedMedia.length > 0) {
        setMediaItems(savedMedia);
      } else {
        // Convert existing images to media items
        setMediaItems((editData.images ?? []).map((url) => ({ url, type: "image" as const })));
      }
      setPatents(editData.patents_certifications ?? []);
      setSections((editData.sections ?? []) as SectionItem[]);
      setApplications(((editData as Record<string, unknown>).applications as { title: string; description: string; image_url: string }[]) ?? []);
      // Load builder pages from saved JSON
      const savedPages = (editData as Record<string, unknown>).builder_pages as BuilderPage[] | undefined;
      if (savedPages && savedPages.length > 0) {
        setBuilderPages(savedPages);
      } else {
        setBuilderPages([]);
      }
      const savedCatalogPages = (editData as Record<string, unknown>).catalog_builder_pages as BuilderPage[] | undefined;
      if (savedCatalogPages && savedCatalogPages.length > 0) {
        setCatalogBuilderPages(savedCatalogPages);
      } else {
        setCatalogBuilderPages([]);
      }
      setPdfUrl((editData as Record<string, unknown>).pdf_url as string ?? "");
      setPdfPreviewImages((editData as Record<string, unknown>).pdf_preview_images as string[] ?? []);
      setFlipbookImages((editData as Record<string, unknown>).flipbook_images as string[] ?? []);
      const imgMap: Record<number, string[]> = {};
      (editData.patents_certifications ?? []).forEach((p, i) => {
        if (p.image_url) imgMap[i] = [p.image_url];
      });
      setPatentImages(imgMap);
      if (editData.category_id) {
        setFilteredSubCats(subCategories.filter((s) => s.parent_id === editData.category_id));
      }
    }
  }, [editData, subCategories]);

  const set = <K extends keyof FormData>(field: K, val: FormData[K]) =>
    setForm((prev) => ({ ...prev, [field]: val }));

  const handleCategoryChange = (catId: string) => {
    const cat = categories.find((c) => c.id === catId);
    set("category_id", catId);
    set("parent_category_id", catId);
    set("category_name", cat ? (lang === "vi" ? cat.name : (cat.name_kr ?? cat.name)) : "");
    set("sub_category_id", "");
    set("sub_category_name", "");
    const filtered = subCategories.filter((s) => s.parent_id === catId);
    setFilteredSubCats(filtered);
  };

  const handleSubCategoryChange = (subId: string) => {
    const sub = subCategories.find((s) => s.id === subId);
    set("sub_category_id", subId);
    set("sub_category_name", sub ? (lang === "vi" ? sub.name : (sub.name_kr ?? sub.name)) : "");
  };

  const handleCategoryAdded = (cat: CategoryRow) => {
    setCategories((prev) => [...prev, cat]);
    setShowAddCategory(false);
    set("category_id", cat.id);
    set("category_name", lang === "vi" ? cat.name : (cat.name_kr ?? cat.name));
  };

  const addPatent = () => setPatents([...patents, { title: "", type: "certification" }]);

  const removePatent = (i: number) => {
    const next = [...patents];
    next.splice(i, 1);
    setPatents(next);
    const imgs = { ...patentImages };
    delete imgs[i];
    setPatentImages(imgs);
  };

  const updatePatent = (i: number, field: keyof PatentCertification, val: string) => {
    const next = [...patents];
    next[i] = { ...next[i], [field]: val };
    setPatents(next);
  };

  // PDF parsing: extract text and auto-fill specs
  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfParsing(true);
    setPdfMessage("");
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfjsLib = await import("pdfjs-dist");
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = "";
      for (let i = 1; i <= Math.min(pdf.numPages, 10); i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item: { str?: string }) => item.str ?? "").join(" ");
        fullText += pageText + "\n";
      }
      const lines = fullText.split(/[\n\r]+/).map((l) => l.trim()).filter(Boolean);
      const parsedSpecs: string[] = [];
      const specPatterns = [/^([^:]{2,40}):\s*(.{1,100})$/, /^([^\t]{2,40})\t(.{1,100})$/];
      lines.forEach((line) => {
        for (const pattern of specPatterns) {
          const match = line.match(pattern);
          if (match) {
            const label = match[1].trim();
            const value = match[2].trim();
            if (label.length > 1 && value.length > 0 && !/^\d+$/.test(label)) {
              parsedSpecs.push(`${label}:${value}`);
            }
            break;
          }
        }
      });
      if (parsedSpecs.length > 0) {
        const existing = form.specsInput.trim();
        const newSpecs = existing ? `${existing}\n${parsedSpecs.join("\n")}` : parsedSpecs.join("\n");
        set("specsInput", newSpecs);
        setPdfMessage(t(`Đã trích xuất ${parsedSpecs.length} thông số từ PDF!`, `PDF에서 ${parsedSpecs.length}개 사양을 추출했습니다!`));
        setActiveTab("specs");
      } else {
        const truncated = fullText.slice(0, 2000);
        set("description_detail", (form.description_detail ?? "") + "\n\n" + truncated);
        setPdfMessage(t("Không tìm thấy thông số dạng bảng. Đã thêm nội dung vào mô tả chi tiết.", "표 형식의 사양을 찾지 못했습니다. 내용을 상세 설명에 추가했습니다."));
      }
    } catch {
      setPdfMessage(t("Lỗi đọc file PDF. Vui lòng thử lại.", "PDF 파일 읽기 오류. 다시 시도해주세요."));
    } finally {
      setPdfParsing(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError(t("Vui lòng nhập tên sản phẩm!", "제품 이름을 입력해주세요!"));
      return;
    }
    setSaving(true);
    setError("");

    try {
      let finalId = form.id.trim();
      if (!finalId) {
        finalId = `sp-${form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30)}-${Date.now()}`;
        setForm((prev) => ({ ...prev, id: finalId }));
      }

      const tags = form.tagsInput.split(",").map((s) => s.trim()).filter(Boolean);
      const specs = form.specsInput
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((line) => {
          const idx = line.indexOf(":");
          if (idx === -1) return { label: line, value: "" };
          return { label: line.slice(0, idx).trim(), value: line.slice(idx + 1).trim() };
        });

      const imagesFromMedia = mediaItems.filter(m => m.type === "image").map(m => m.url);
      const finalImages = imagesFromMedia.length > 0 ? imagesFromMedia : images;

      const patentsFinal = patents.map((p, i) => ({
        ...p,
        image_url: patentImages[i]?.[0] ?? p.image_url ?? "",
      }));

      const selectedCat = categories.find((c) => c.id === form.category_id);
      const catName = selectedCat ? (lang === "vi" ? selectedCat.name : (selectedCat.name_kr ?? selectedCat.name)) : (form.category_name ?? "");

      const payload: Record<string, unknown> = {
        id: finalId,
        name: form.name.trim(),
        name_kr: form.name_kr ?? "",
        category_id: form.category_id ?? "",
        category_name: catName,
        sub_category_id: form.sub_category_id ?? "",
        sub_category_name: form.sub_category_name ?? "",
        company_name: form.company_name ?? "",
        company_code: form.company_code ?? "",
        company_logo: form.company_logo ?? "",
        manager_code: form.manager_code ?? "",
        manager_phone: form.manager_phone ?? "",
        manager_email: form.manager_email ?? "",
        price: form.price ?? "",
        description: form.description ?? "",
        description_detail: form.description_detail ?? "",
        images: finalImages,
        media_items: mediaItems,
        tags,
        specs,
        registered_date: form.registered_date ?? new Date().toISOString().slice(0, 10),
        is_featured: form.is_featured ?? false,
        original_link: form.original_link ?? "",
        card_link: form.card_link ?? "",
        hero_tagline: form.hero_tagline ?? "",
        sections: sections as SanPhamHanQuoc["sections"],
        patents_certifications: patentsFinal,
        applications: applications.filter((a) => a.title.trim()),
        display_mode: form.display_mode ?? "default",
        pdf_url: pdfUrl || null,
        external_url: form.external_url ?? "",
      };

      if (builderPages.length > 0) {
        payload.builder_pages = builderPages;
      }
      if (catalogBuilderPages.length > 0) {
        payload.catalog_builder_pages = catalogBuilderPages;
      }
      if (flipbookImages.length > 0) {
        payload.flipbook_images = flipbookImages;
      }
      if (pdfPreviewImages.length > 0) {
        payload.pdf_preview_images = pdfPreviewImages;
        payload.pdf_pages = pdfPreviewImages.map((_, i) => i + 1);
      }

      const { error: dbErr } = await supabase
        .from("san_pham_han_quoc")
        .upsert(payload, { onConflict: "id" });
      if (dbErr) {
        setError(dbErr.message);
        return;
      }
      onSuccess();
    } catch (err) {
      setError((err as Error).message || t("Lỗi không xác định khi lưu.", "저장 중 알 수 없는 오류가 발생했습니다."));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

  const baseTabs = [
    { id: "basic" as const, label: t("Thông tin cơ bản", "기본 정보"), icon: "ri-information-line" },
    { id: "media" as const, label: t("Hình ảnh", "이미지"), icon: "ri-image-line" },
    { id: "sections" as const, label: t("Story", "스토리"), icon: "ri-layout-column-line" },
    { id: "applications" as const, label: t("Ứng dụng", "응용"), icon: "ri-apps-line" },
    { id: "patents" as const, label: t("Chứng nhận", "특허"), icon: "ri-award-line" },
    { id: "catalog" as const, label: t("Trang Chi tiết - Link", "상세 페이지 - 링크"), icon: "ri-booklet-line" },
  ];

  const tabs = [
    ...baseTabs,
    { id: "detail" as const, label: t("Trang Chi Tiết", "상세 페이지"), icon: "ri-pages-line" },
    { id: "flipbook" as const, label: t("Flipbook", "플립북"), icon: "ri-book-open-line" },
  ];

  return (
    <>
      {showAddCategory && (
        <AddCategoryModal
          lang={lang}
          onClose={() => setShowAddCategory(false)}
          onSaved={handleCategoryAdded}
        />
      )}
      {showPreviewModal && builderPages.length > 0 && (
        <BuilderPreviewModal
          pages={builderPages}
          productName={form.name}
          onClose={() => setShowPreviewModal(false)}
        />
      )}
      {showCatalogPreviewModal && catalogBuilderPages.length > 0 && (
        <BuilderPreviewModal
          pages={catalogBuilderPages}
          productName={form.name}
          onClose={() => setShowCatalogPreviewModal(false)}
        />
      )}
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-[98vw] max-w-[1800px] flex flex-col h-[90vh]">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                <i className="ri-box-3-line text-emerald-700" />
              </div>
              <div>
                <h2 className="font-bold text-gray-800">
                  {editData ? t("Chỉnh sửa sản phẩm", "제품 수정") : t("Thêm sản phẩm mới", "새 제품 추가")}
                </h2>
                {form.display_mode === "advanced" && (
                  <p className="text-[10px] text-[#004A99] font-medium mt-0.5">
                    <i className="ri-pages-line mr-1" />
                    {t("Chế độ: Trang chi tiết nâng cao", "모드: 고급 상세 페이지")}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {activeTab === "detail" && (
                <button
                  type="button"
                  onClick={() => setShowPreviewModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-eye-line" />
                  {t("Preview", "미리보기")}
                </button>
              )}
              {activeTab === "catalog" && (
                <button
                  type="button"
                  onClick={() => setShowCatalogPreviewModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-eye-line" />
                  {t("Preview Catalog", "카탈로그 미리보기")}
                </button>
              )}
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
              <i className="ri-close-line text-gray-500" />
            </button>
          </div>
        </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-100 flex-shrink-0 px-4 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-[#004A99] text-[#004A99]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <i className={tab.icon} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Form */}
          {(
            <div className={`flex-1 overflow-y-auto min-h-0 ${activeTab === "detail" ? "px-2 py-4 flex flex-col" : "p-6 space-y-4"}`}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>
              )}

              {/* ── TAB: BASIC INFO ── */}
              {activeTab === "basic" && (
                <>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className={labelCls}>{t("Danh mục *", "카테고리 *")}</label>
                      <div className="flex gap-2 items-center">
                        <select
                          className={`${inputCls} flex-1`}
                          value={form.category_id ?? ""}
                          onChange={(e) => handleCategoryChange(e.target.value)}
                        >
                          <option value="">{t("-- Chọn danh mục --", "-- 카테고리 선택 --")}</option>
                          {categories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {lang === "vi" ? c.name : (c.name_kr ?? c.name)}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowAddCategory(true)}
                          title={t("Thêm danh mục mới", "새 카테고리 추가")}
                          className="w-9 h-9 flex items-center justify-center bg-[#004A99]/10 hover:bg-[#004A99]/20 text-[#004A99] rounded-lg transition-colors cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-add-line" />
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Display Mode Selector */}
                  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50">
                    <label className={labelCls}>{t("Kiểu hiển thị sản phẩm", "제품 표시 유형")}</label>
                    <div className="flex gap-3 mt-2">
                      <button
                        type="button"
                        onClick={() => {
                          set("display_mode", "default");
                          setActiveTab("basic");
                        }}
                        className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all cursor-pointer ${
                          form.display_mode !== "advanced"
                            ? "border-[#004A99] bg-[#EDF2FB] text-[#004A99]"
                            : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        <i className="ri-layout-4-line text-lg" />
                        <div className="text-left">
                          <p className="text-xs font-bold">{t("Layout mặc định", "기본 레이아웃")}</p>
                          <p className="text-[10px] font-normal opacity-70">{t("Hiển thị theo field hiện tại", "기존 필드 표시")}</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          set("display_mode", "advanced");
                          if (builderPages.length === 0) {
                            setBuilderPages([{ id: "page-1", name: "Trang 1", blocks: [] }]);
                          }
                        }}
                        className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all cursor-pointer ${
                          form.display_mode === "advanced"
                            ? "border-[#004A99] bg-[#EDF2FB] text-[#004A99]"
                            : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        <i className="ri-pages-line text-lg" />
                        <div className="text-left">
                          <p className="text-xs font-bold">{t("Trang chi tiết nâng cao", "고급 상세 페이지")}</p>
                          <p className="text-[10px] font-normal opacity-70">{t("Tùy chỉnh như landing page", "랜딩 페이지처럼 커스터마이징")}</p>
                        </div>
                      </button>
                    </div>
                    {form.display_mode === "advanced" && (
                      <p className="text-[10px] text-[#004A99] mt-2 flex items-center gap-1">
                        <i className="ri-information-line" />
                        {t("Tab Trang Chi Tiết đã được bật. Bạn có thể thiết kế trang sản phẩm riêng biệt.", "상세 페이지 탭이 활성화되었습니다. 별도의 제품 페이지를 디자인할 수 있습니다.")}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>{t("Tên sản phẩm (VI) *", "제품명 (VI) *")}</label>
                      <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>{t("Tên sản phẩm (KR)", "제품명 (KR)")}</label>
                      <input className={inputCls} value={form.name_kr ?? ""} onChange={(e) => set("name_kr", e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>{t("Danh mục con", "하위 카테고리")}</label>
                      <select
                        className={inputCls}
                        value={form.sub_category_id ?? ""}
                        onChange={(e) => handleSubCategoryChange(e.target.value)}
                        disabled={!form.category_id}
                      >
                        <option value="">
                          {!form.category_id
                            ? t("-- Chọn danh mục chính trước --", "-- 먼저 카테고리를 선택하세요 --")
                            : filteredSubCats.length === 0
                            ? t("-- Không có danh mục con --", "-- 하위 카테고리 없음 --")
                            : t("-- Chọn danh mục con --", "-- 하위 카테고리 선택 --")}
                        </option>
                        {filteredSubCats.map((s) => (
                          <option key={s.id} value={s.id}>
                            {lang === "vi" ? s.name : (s.name_kr ?? s.name)}
                          </option>
                        ))}
                      </select>
                      {form.sub_category_id && (
                        <p className="text-[10px] text-[#004A99] mt-1">✓ {form.sub_category_name}</p>
                      )}
                    </div>
                    <div>
                      <label className={labelCls}>{t("Tên công ty *", "회사명 *")}</label>
                      <input className={inputCls} value={form.company_name ?? ""} onChange={(e) => set("company_name", e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={labelCls}>{t("SĐT quản lý", "담당자 전화")}</label>
                      <input className={inputCls} value={form.manager_phone ?? ""} onChange={(e) => set("manager_phone", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>{t("Email quản lý", "담당자 이메일")}</label>
                      <input className={inputCls} value={form.manager_email ?? ""} onChange={(e) => set("manager_email", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>{t("Giá", "가격")}</label>
                      <input className={inputCls} value={form.price ?? ""} onChange={(e) => set("price", e.target.value)} placeholder={t("Liên hệ", "문의")} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{t("Mô tả ngắn", "간단한 설명")}</label>
                    <textarea className={`${inputCls} resize-none`} rows={2} value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} />
                  </div>
                  <RichTextEditor
                    value={form.description_detail ?? ""}
                    onChange={(html) => set("description_detail", html)}
                    placeholder={t("Nhập mô tả chi tiết sản phẩm...", "제품 상세 설명을 입력하세요...")}
                    minHeight={280}
                    folder="products"
                    label={t("Mô tả chi tiết", "상세 설명")}
                  />
                  <div>
                    <label className={labelCls}>{t("Tags (cách bằng dấu phẩy)", "태그 (쉼표로 구분)")}</label>
                    <input className={inputCls} value={form.tagsInput} onChange={(e) => set("tagsInput", e.target.value)} placeholder="Nano, Carbon, Composite" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>{t("Link sản phẩm gốc", "원본 제품 링크")}</label>
                      <input className={inputCls} value={form.original_link ?? ""} onChange={(e) => set("original_link", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Hero Tagline</label>
                      <input className={inputCls} value={form.hero_tagline ?? ""} onChange={(e) => set("hero_tagline", e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>{t("Ngày đăng ký", "등록일")}</label>
                      <input type="date" className={inputCls} value={form.registered_date ?? ""} onChange={(e) => set("registered_date", e.target.value)} />
                    </div>
                    <div className="flex flex-col justify-end pb-1">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.is_featured ?? false} onChange={(e) => set("is_featured", e.target.checked)} className="w-4 h-4 rounded" />
                        <span className="text-xs font-semibold text-gray-700">{t("Sản phẩm nổi bật", "추천 제품")}</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* ── TAB: MEDIA ── */}
              {activeTab === "media" && (
                <div className="space-y-6">
                  <div className="bg-[#EDF2FB] rounded-xl p-4 border border-[#004A99]/10">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-information-line text-[#004A99]" />
                      <p className="text-xs font-semibold text-[#004A99]">{t("Hướng dẫn tải media", "미디어 업로드 안내")}</p>
                    </div>
                    <p className="text-xs text-[#004A99]/70">{t("Hỗ trợ cả ảnh và video. File đầu tiên sẽ là ảnh/video chính. Video sẽ tự phát khi hover.", "이미지와 동영상 모두 지원. 첫 번째 파일이 메인으로 표시됩니다.")}</p>
                  </div>
                  <MediaUploader
                    items={mediaItems}
                    onChange={setMediaItems}
                    maxItems={8}
                    folder="products"
                    label={t("Hình ảnh & Video sản phẩm (tối đa 8 file)", "제품 이미지 & 동영상 (최대 8개)")}
                  />
                  <div>
                    <label className={labelCls}>{t("Logo công ty (URL)", "회사 로고 URL")}</label>
                    <div className="flex items-center gap-3">
                      <input
                        className={`${inputCls} flex-1`}
                        placeholder="https://..."
                        value={form.company_logo ?? ""}
                        onChange={(e) => { set("company_logo", e.target.value); setLogoAutoFilled(false); }}
                      />
                      {form.company_logo && (
                        <img
                          src={form.company_logo}
                          alt="Logo preview"
                          className="w-10 h-10 rounded-lg object-contain border border-gray-200 bg-white flex-shrink-0"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                      )}
                    </div>
                    {logoAutoFilled && (
                      <p className="text-[10px] text-green-600 mt-1 flex items-center gap-1">
                        <i className="ri-check-line" />
                        {t("Logo tự động điền từ dữ liệu công ty", "회사 데이터에서 자동 입력된 로고")}
                      </p>
                    )}
                  </div>
                </div>
              )}



              {/* ── TAB: SECTIONS ── */}
              {activeTab === "sections" && (
                <div className="space-y-3">
                  <SectionsRepeater lang={lang} sections={sections} onChange={setSections} />
                </div>
              )}

              {/* ── TAB: APPLICATIONS ── */}
              {activeTab === "applications" && (
                <div className="space-y-4">
                  {applications.map((app, i) => (
                    <div key={i} className="border border-gray-200 rounded-xl p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-700">{t(`Ứng dụng ${i + 1}`, `응용 ${i + 1}`)}</span>
                        <button onClick={() => setApplications(applications.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 cursor-pointer text-xs"><i className="ri-delete-bin-line" /> {t("Xóa", "삭제")}</button>
                      </div>
                      <div>
                        <label className={labelCls}>{t("Tiêu đề ứng dụng *", "응용 제목 *")}</label>
                        <input className={inputCls} value={app.title} onChange={(e) => { const next = [...applications]; next[i] = { ...next[i], title: e.target.value }; setApplications(next); }} />
                      </div>
                      <RichTextEditor value={app.description} onChange={(html) => { const next = [...applications]; next[i] = { ...next[i], description: html }; setApplications(next); }} placeholder="Mô tả ứng dụng..." minHeight={120} folder="applications" />
                      <ImageUploader images={app.image_url ? [app.image_url] : []} onChange={(urls) => { const next = [...applications]; next[i] = { ...next[i], image_url: urls[0] ?? "" }; setApplications(next); }} maxImages={1} folder="applications" label={t("Hình ảnh nền", "배경 이미지")} />
                    </div>
                  ))}
                  <button type="button" onClick={() => setApplications([...applications, { title: "", description: "", image_url: "" }])} className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-[#004A99] hover:text-[#004A99] transition-colors cursor-pointer">
                    <i className="ri-add-line" /> {t("Thêm ứng dụng mới", "새 응용 추가")}
                  </button>
                </div>
              )}

              {/* ── TAB: CATALOG ── */}
              {activeTab === "catalog" && (
                <div className="gap-4 h-full flex flex-col" style={{ minHeight: 500 }}>
                  <div className="bg-[#EDF2FB] rounded-xl p-4 border border-[#004A99]/10 flex items-start gap-3">
                    <i className="ri-information-line text-[#004A99] mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-[#004A99]">{t("Catalog B2B", "B2B 카탈로그")}</p>
                      <p className="text-xs text-[#004A99]/70 mt-1">
                        {t("Thiết kế trang catalog B2B tùy chỉnh. Kéo thả block, upload ảnh/video để tạo catalog chuyên nghiệp cho đối tác thương mại.", "B2B 카탈로그 페이지를 디자인하세요. 블록을 드래그하고 이미지/동영상을 업로드하여 거래 파트너를 위한 전문적인 카탈로그를 만드세요.")}
                      </p>
                    </div>
                  </div>

                  {/* Visual Builder */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden flex-1" style={{ minHeight: 400 }}>
                    <VisualBuilder
                      lang={lang}
                      pages={catalogBuilderPages}
                      productName={form.name}
                      onChange={setCatalogBuilderPages}
                    />
                  </div>

                  {/* Preview Button */}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowCatalogPreviewModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-eye-line" />
                      {t("Preview Catalog", "카탈로그 미리보기")}
                    </button>
                  </div>
                </div>
              )}

              {/* ── TAB: DETAIL (Advanced Page Builder) ── */}
              {activeTab === "detail" && (
                <div className="gap-4 h-full flex flex-col" style={{ minHeight: 500 }}>
                  <div className="bg-[#EDF2FB] rounded-xl p-4 border border-[#004A99]/10 flex items-start gap-3">
                    <i className="ri-information-line text-[#004A99] mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-[#004A99]">{t("Trang Chi Tiết Nâng Cao", "고급 상세 페이지")}</p>
                      <p className="text-xs text-[#004A99]/70 mt-1">
                        {t("Thiết kế trang sản phẩm như landing page. Kéo thả block, upload ảnh/video, nhúng PDF. Khi lưu, frontend sẽ hiển thị thay thế layout mặc định.", "랜딩 페이지처럼 제품 페이지를 디자인하세요. 블록을 드래그하고 이미지/동영상을 업로드하고 PDF를 임베드하세요.")}
                      </p>
                    </div>
                  </div>

                  {/* Read-only category / company summary */}
                  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-link-m text-gray-400 text-sm" />
                      <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">{t("Thông tin liên kết", "연결 정보")}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-gray-400 text-[10px]">{t("Danh mục chính", "주요 카테고리")}</span>
                        <span className="font-semibold text-gray-800">{form.category_name || <span className="text-gray-300 italic">—</span>}</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-gray-400 text-[10px]">{t("Danh mục con", "하위 카테고리")}</span>
                        <span className="font-semibold text-gray-800">{form.sub_category_name || <span className="text-gray-300 italic">—</span>}</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-gray-400 text-[10px]">{t("Tên công ty", "회사명")}</span>
                        <span className="font-semibold text-gray-800">{form.company_name || <span className="text-gray-300 italic">—</span>}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-[10px]">{t("Logo công ty", "회사 로고")}</span>
                        {form.company_logo ? (
                          <img src={form.company_logo} alt="logo" className="w-8 h-8 rounded-lg object-contain border border-gray-200 bg-white" />
                        ) : (
                          <span className="text-gray-300 italic">—</span>
                        )}
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2">
                      {t("* Dữ liệu trên được lấy từ tab Thông tin cơ bản. Chỉnh sửa tại tab đó.", "* 위 데이터는 기본 정보 탭에서 가져왔습니다. 해당 탭에서 수정하세요.")}
                    </p>
                  </div>

                  {/* PDF Upload & Convert */}
                  <div className="border border-gray-200 rounded-xl p-4">
                    <label className={labelCls}>{t("Upload Brochure / Catalog PDF", "브로셔 / 카탈로그 PDF 업로드")}</label>
                    <p className="text-[10px] text-gray-400 mb-2">
                      {t("Hệ thống tự động chuyển từng trang PDF thành ảnh chất lượng cao và thêm vào trang chi tiết.", "시스템이 각 PDF 페이지를 고품질 이미지로 자동 변환하여 상세 페이지에 추가합니다.")}
                    </p>
                    <div className="flex gap-2 items-center">
                      <input
                        ref={pdfConvertInputRef}
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.type !== "application/pdf") {
                            setPdfMessage(t("File không phải PDF.", "PDF 파일이 아닙니다."));
                            if (pdfConvertInputRef.current) pdfConvertInputRef.current.value = "";
                            return;
                          }
                          if (file.size > 50 * 1024 * 1024) {
                            setPdfMessage(t("File quá lớn (tối đa 50MB).", "파일이 너무 큽니다(최대 50MB)."));
                            if (pdfConvertInputRef.current) pdfConvertInputRef.current.value = "";
                            return;
                          }
                          setPdfConverting(true);
                          setPdfMessage("");
                          let totalPages = 0;
                          try {
                            // 1. Upload original PDF
                            const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
                            const pdfPath = `product-pdfs/${Date.now()}-${safeName}`;
                            const { error: pdfUploadErr } = await supabase.storage
                              .from("bizwell-images")
                              .upload(pdfPath, file, { contentType: "application/pdf", cacheControl: "3600" });
                            if (pdfUploadErr) throw new Error(pdfUploadErr.message);
                            const { data: pdfUrlData } = supabase.storage.from("bizwell-images").getPublicUrl(pdfPath);
                            const uploadedPdfUrl = pdfUrlData?.publicUrl ?? "";
                            setPdfUrl(uploadedPdfUrl);

                            // 2. Convert to images
                            const arrayBuffer = await file.arrayBuffer();
                            const pdfjsLib = await import("pdfjs-dist");
                            pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs`;
                            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
                            totalPages = pdf.numPages;
                            const pageUrls: string[] = [];
                            for (let i = 1; i <= totalPages; i++) {
                              const page = await pdf.getPage(i);
                              const scale = 2.5;
                              const viewport = page.getViewport({ scale });
                              const canvas = document.createElement("canvas");
                              const ctx = canvas.getContext("2d");
                              if (!ctx) continue;
                              const dpr = window.devicePixelRatio || 1;
                              canvas.width = viewport.width * dpr;
                              canvas.height = viewport.height * dpr;
                              canvas.style.width = `${viewport.width}px`;
                              canvas.style.height = `${viewport.height}px`;
                              ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
                              await page.render({ canvasContext: ctx, viewport }).promise;
                              const blob = await new Promise<Blob | null>((resolve) =>
                                canvas.toBlob((b) => resolve(b), "image/png")
                              );
                              if (!blob) continue;
                              const fileName = `pdf-preview-${Date.now()}-page-${i}.png`;
                              const { error: imgUploadErr } = await supabase.storage
                                .from("bizwell-images")
                                .upload(`pdf-previews/${fileName}`, blob, { contentType: "image/png" });
                              if (imgUploadErr) continue;
                              const { data: urlData } = supabase.storage
                                .from("bizwell-images")
                                .getPublicUrl(`pdf-previews/${fileName}`);
                              if (urlData?.publicUrl) pageUrls.push(urlData.publicUrl);
                            }
                            setPdfPreviewImages(pageUrls);
                            if (pageUrls.length > 0) {
                              const newBlocks = pageUrls.map((url, idx) => ({
                                id: `block-${Date.now()}-${idx}`,
                                data: {
                                  type: "story" as const,
                                  items: [{
                                    id: `story-${Date.now()}-${idx}`,
                                    title: "",
                                    content: "",
                                    image: url,
                                    imagePosition: "left" as const,
                                    accent: "",
                                  }],
                                },
                              }));
                              setBuilderPages((prev) => {
                                const firstPage = prev[0] ?? { id: "page-1", name: "Trang 1", blocks: [] };
                                return [{ ...firstPage, blocks: [...firstPage.blocks, ...newBlocks] }, ...prev.slice(1)];
                              });
                              setPdfMessage(t(`Đã chuyển đổi ${pageUrls.length}/${totalPages} trang PDF thành ảnh! File PDF gốc đã được lưu.`, `${pageUrls.length}/${totalPages}개의 PDF 페이지를 이미지로 변환했습니다! 원본 PDF가 저장되었습니다.`));
                            } else {
                              setPdfMessage(t("Không thể chuyển đổi PDF sang ảnh. File PDF gốc đã được lưu.", "PDF를 이미지로 변환할 수 없습니다. 원본 PDF가 저장되었습니다."));
                            }
                          } catch (err) {
                            setPdfMessage(t(`Lỗi xử lý PDF: ${(err as Error).message}. Vui lòng thử file khác.`, `PDF 처리 오류: ${(err as Error).message}. 다른 파일을 시도해주세요.`));
                          } finally {
                            setPdfConverting(false);
                            if (pdfConvertInputRef.current) pdfConvertInputRef.current.value = "";
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => pdfConvertInputRef.current?.click()}
                        disabled={pdfConverting}
                        className="flex items-center gap-2 px-4 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-semibold rounded-lg border border-amber-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        {pdfConverting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-file-upload-line" />}
                        {pdfConverting ? t("Đang xử lý PDF...", "PDF 처리 중...") : t("Chọn file PDF", "PDF 파일 선택")}
                      </button>
                      {pdfMessage && (
                        <span className={`text-xs ${pdfMessage.includes("Đã chuyển") || pdfMessage.includes("chuyển đổi") || pdfMessage.includes("변환") ? "text-green-600" : "text-red-500"}`}>
                          {pdfMessage}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* External URL */}
                  <div className="border border-gray-200 rounded-xl p-4">
                    <label className={labelCls}>{t("Link ngoài (Website / Brochure / PDF online)", "외부 링크 (웹사이트 / 브로셔 / 온라인 PDF)")}</label>
                    <input
                      className={inputCls}
                      value={form.external_url ?? ""}
                      onChange={(e) => set("external_url", e.target.value)}
                      placeholder="https://example.com/brochure.pdf"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">
                      {t("Nếu nhập link PDF online hoặc website, hệ thống sẽ hiển thị nút mở link trong trang chi tiết.", "온라인 PDF 링크나 웹사이트를 입력하면 상세 페이지에 링크 열기 버튼이 표시됩니다.")}
                    </p>
                  </div>

                  {/* Visual Builder */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden flex-1" style={{ minHeight: 400 }}>
                    <VisualBuilder
                      lang={lang}
                      pages={builderPages}
                      productName={form.name}
                      onChange={setBuilderPages}
                    />
                  </div>

                  {/* Preview Button */}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowPreviewModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-eye-line" />
                      {t("Preview Trang Chi Tiết", "상세 페이지 미리보기")}
                    </button>
                  </div>
                </div>
              )}
              {/* ── TAB: FLIPBOOK ── */}
              {activeTab === "flipbook" && (
                <div className="space-y-6">
                  <div className="bg-[#EDF2FB] rounded-xl p-4 border border-[#004A99]/10">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-information-line text-[#004A99]" />
                      <p className="text-xs font-semibold text-[#004A99]">{t("Hướng dẫn tạo Flipbook", "플립북 생성 안내")}</p>
                    </div>
                    <p className="text-xs text-[#004A99]/70">
                      {t("Tải lên các hình ảnh để tạo từng trang Flipbook. Mỗi hình ảnh sẽ trở thành một trang riêng biệt trong Flipbook. Hệ thống sẽ tự động fit ảnh vừa khung trang.", "플립북 페이지를 만들 이미지를 업로드하세요. 각 이미지는 플립북의 별도 페이지가 되며, 시스템이 자동으로 페이지 크기에 맞게 조정합니다.")}
                    </p>
                  </div>



              <ImageUploader
                    images={flipbookImages}
                    onChange={setFlipbookImages}
                    maxImages={200}
                    folder="flipbook"
                    label={t("Hình ảnh trang Flipbook (tối đa 200 ảnh)", "플립북 페이지 이미지 (최대 200장)")}
                  />

                  {/* Drag-and-drop sorter */}
                  {flipbookImages.length > 0 && (
                    <FlipbookImageSorter
                      images={flipbookImages}
                      onChange={setFlipbookImages}
                    />
                  )}

                  {/* ── QR Code Section ── */}
                  <div className="border border-gray-200 rounded-xl p-5 bg-white">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-6 h-6 flex items-center justify-center bg-[#004A99]/10 rounded-lg">
                        <i className="ri-qr-code-line text-[#004A99] text-sm" />
                      </div>
                      <p className="text-xs font-bold text-gray-800">{t("Mã QR cho Flipbook", "플립북 QR 코드")}</p>
                    </div>

                    {flipbookQrUrl ? (
                      <div className="flex flex-col sm:flex-row gap-5 items-start">
                        {/* QR Image */}
                        <div className="flex flex-col items-center gap-2 flex-shrink-0">
                          {qrDataUrl ? (
                            <div className="border border-gray-200 rounded-xl p-2 bg-white shadow-sm">
                              <img
                                src={qrDataUrl}
                                alt="Flipbook QR Code"
                                className="w-40 h-40 rounded-lg"
                              />
                            </div>
                          ) : (
                            <div className="w-40 h-40 flex items-center justify-center bg-gray-50 rounded-xl border border-gray-200">
                              <i className="ri-loader-4-line animate-spin text-gray-400 text-2xl" />
                            </div>
                          )}
                          <p className="text-[9px] text-gray-400 text-center max-w-[160px] break-all leading-relaxed">
                            {flipbookQrUrl}
                          </p>
                        </div>

                        {/* Action Buttons & Info */}
                        <div className="flex flex-col gap-3 flex-1">
                          <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                            <p className="text-[10px] font-semibold text-[#004A99] mb-1">
                              <i className="ri-information-line mr-1" />
                              {t("Cơ chế hoạt động", "작동 방식")}
                            </p>
                            <p className="text-[10px] text-[#004A99]/70 leading-relaxed">
                              {t(
                                "Link trong mã QR cố định theo tên sản phẩm. Khi bạn cập nhật hình ảnh Flipbook, QR cũ vẫn dẫn đến nội dung mới nhất.",
                                "QR 코드의 링크는 제품명 기반으로 고정됩니다. 이미지를 업데이트해도 기존 QR로 최신 콘텐츠에 접근할 수 있습니다."
                              )}
                            </p>
                          </div>

                          <div className="flex flex-col gap-2">
                            <button
                              type="button"
                              onClick={handleDownloadQR}
                              disabled={!qrDataUrl}
                              className="flex items-center gap-2 px-4 py-2.5 bg-[#004A99] hover:bg-[#003a7a] disabled:opacity-50 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                            >
                              <i className="ri-download-2-line" />
                              {t("Tải mã QR (.PNG)", "QR 다운로드 (.PNG)")}
                            </button>
                            <button
                              type="button"
                              onClick={handleCopyQrUrl}
                              className={`flex items-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors border ${
                                qrCopied
                                  ? "bg-green-50 border-green-300 text-green-700"
                                  : "bg-white border-gray-200 text-gray-700 hover:border-[#004A99] hover:text-[#004A99]"
                              }`}
                            >
                              <i className={qrCopied ? "ri-check-line" : "ri-links-line"} />
                              {qrCopied
                                ? t("Đã sao chép!", "복사됨!")
                                : t("Sao chép Link QR", "QR 링크 복사")
                              }
                            </button>
                          </div>

                          <p className="text-[9px] text-gray-400">
                            {t(
                              "* Lưu sản phẩm trước khi in mã QR để đảm bảo link hoạt động.",
                              "* QR 링크가 작동하도록 제품을 먼저 저장하세요."
                            )}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 py-4 text-gray-400">
                        <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
                          <i className="ri-qr-code-line text-gray-300 text-lg" />
                        </div>
                        <p className="text-xs">
                          {t(
                            "Nhập tên sản phẩm và tên công ty để tự động tạo mã QR.",
                            "제품명과 회사명을 입력하면 QR 코드가 자동 생성됩니다."
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
            <p className="text-xs text-gray-400">
              {images.length} {t("ảnh", "이미지")}
              {builderPages.length > 0 && ` · ${builderPages.reduce((acc, p) => acc + p.blocks.length, 0)} detail blocks`}
              {catalogBuilderPages.length > 0 && ` · ${catalogBuilderPages.reduce((acc, p) => acc + p.blocks.length, 0)} catalog blocks`}
              {flipbookImages.length > 0 && ` · ${flipbookImages.length} flipbook images`}
              {builderPages.length === 0 && catalogBuilderPages.length === 0 && flipbookImages.length === 0 && ` · ${patents.length} ${t("bằng sáng chế", "특허")}`}
            </p>
            <div className="flex gap-3">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap transition-colors">{t("Hủy", "취소")}</button>
              <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-[#004A99] hover:bg-[#003a7a] text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2 transition-colors">
                {saving && <i className="ri-loader-4-line animate-spin" />}
                {t("Lưu sản phẩm", "저장")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductFormModal;
